import { useContractRead, useContractReads } from 'wagmi';
import { ADDRESSES, CONFIG } from '../config';
import { Abi } from 'viem';
import { useMemo, useEffect } from 'react';

export interface EntityDetails {
  address: `0x${string}`;
  name: string;
  type: string;
  description: string;
  products: ProductDetails[];
  owner: `0x${string}`;
  resell_value: bigint;
}

export interface ProductDetails {
  address: `0x${string}`;
  name: string;
  description: string;
  className: string;
  entityAddress: `0x${string}`;
  assets: {
    address: `0x${string}`;
    balance: bigint;
    symbol: string;
  }[];
  productCount: number;
  owner: `0x${string}`;
  resell_value: bigint;
  items: {
    id: number;
    address: `0x${string}`;
    name: string;
    description: string;
    owner: `0x${string}`;
    resellPrice: bigint;
  }[];
  // Legacy fields for backward compatibility
  assetBalance?: bigint;
  assetSymbol?: string;
}

export function useEntityDetails() {
  // Get all entity addresses
  const { data: entityAddresses } = useContractRead({
    address: ADDRESSES.TRINITY_MASTER,
    abi: CONFIG.TRINITY_ABI.abi as unknown as Abi,
    functionName: 'getEntities',
  });

  // Get entity details
  const { data: entities } = useContractReads({
    contracts: ((entityAddresses as `0x${string}`[]) || []).map((address) => ({
      address,
      abi: CONFIG.TRINITY_ENTITY_ABI.abi as unknown as Abi,
      functionName: 'name',
    })),
  });

  // Get entity types
  const { data: entityTypes } = useContractReads({
    contracts: ((entityAddresses as `0x${string}`[]) || []).map((address) => ({
      address,
      abi: CONFIG.TRINITY_ENTITY_ABI.abi as unknown as Abi,
      functionName: 'type',
    })),
  });

  // Get entity descriptions
  const { data: entityDescriptions } = useContractReads({
    contracts: ((entityAddresses as `0x${string}`[]) || []).map((address) => ({
      address,
      abi: CONFIG.TRINITY_ENTITY_ABI.abi as unknown as Abi,
      functionName: 'description',
    })),
  });

  // Get entity owners
  const { data: entityOwners } = useContractReads({
    contracts: ((entityAddresses as `0x${string}`[]) || []).map((address) => ({
      address,
      abi: CONFIG.TRINITY_ENTITY_ABI.abi as unknown as Abi,
      functionName: 'getOwner',
    })),
  });

  // Get entity resell values
  const { data: entityResellValues } = useContractReads({
    contracts: ((entityAddresses as `0x${string}`[]) || []).map((address) => ({
      address,
      abi: CONFIG.TRINITY_ENTITY_ABI.abi as unknown as Abi,
      functionName: 'resell_value',
    })),
  });

  // Get product addresses for each entity
  const { data: productAddresses } = useContractReads({
    contracts: ((entityAddresses as `0x${string}`[]) || []).map((address) => ({
      address,
      abi: CONFIG.TRINITY_ENTITY_ABI.abi as unknown as Abi,
      functionName: 'getProducts',
    })),
  });

  // Get product details (name and className)
  const flatProductAddresses = useMemo(() => 
    productAddresses?.flatMap((addresses, i) => 
      ((addresses?.result as `0x${string}`[]) || []).map(addr => ({
        address: addr,
        entityAddress: (entityAddresses as `0x${string}`[])?.[i],
      }))
    ) || []
  , [productAddresses, entityAddresses]);

  // Get product names
  const { data: productNames } = useContractReads({
    contracts: flatProductAddresses.map(({ address }) => ({
      address,
      abi: CONFIG.TRINITY_PRODUCT_ABI.abi as unknown as Abi,
      functionName: 'name',
    })),
  });

  // Get product descriptions
  const { data: productDescriptions } = useContractReads({
    contracts: flatProductAddresses.map(({ address }) => ({
      address,
      abi: CONFIG.TRINITY_PRODUCT_ABI.abi as unknown as Abi,
      functionName: 'description',
    })),
  });

  // Get product classNames
  const { data: productClassNames } = useContractReads({
    contracts: flatProductAddresses.map(({ address }) => ({
      address,
      abi: CONFIG.TRINITY_PRODUCT_ABI.abi as unknown as Abi,
      functionName: 'className',
    })),
  });

  // Get product counts
  const { data: productCounts } = useContractReads({
    contracts: flatProductAddresses.map(({ address }) => ({
      address,
      abi: CONFIG.TRINITY_PRODUCT_ABI.abi as unknown as Abi,
      functionName: 'getProductCount',
    })),
  });

  // Get product owners
  const { data: productOwners } = useContractReads({
    contracts: flatProductAddresses.map(({ address }) => ({
      address,
      abi: CONFIG.TRINITY_PRODUCT_ABI.abi as unknown as Abi,
      functionName: 'getOwner',
    })),
  });

  // Get product resell values
  const { data: productResellValues } = useContractReads({
    contracts: flatProductAddresses.map(({ address }) => ({
      address,
      abi: CONFIG.TRINITY_PRODUCT_ABI.abi as unknown as Abi,
      functionName: 'resell_value',
    })),
  });

  // Get product assets using the correct ABI based on class name
  const { data: productAssets } = useContractReads({
    contracts: flatProductAddresses.map(({ address }, index) => {
      const className = productClassNames?.[index]?.result as string;
      const abi = className === 'TrinityBond' 
        ? CONFIG.TRINITY_BOND_ABI.abi 
        : CONFIG.TRINITY_PRODUCT_ABI.abi;
      
      return {
        address,
        abi: abi as unknown as Abi,
        functionName: 'getSupportedAssets',
      };
    }),
  });

  // Get asset balances and symbols for all supported assets
  const allAssetAddresses = useMemo(() => {
    const addresses = new Set<`0x${string}`>();
    productAssets?.forEach(assets => {
      if (assets?.result) {
        (assets.result as `0x${string}`[]).forEach(addr => addresses.add(addr));
      }
    });
    return Array.from(addresses);
  }, [productAssets]);

  // Get balances for all assets
  const { data: allAssetBalances } = useContractReads({
    contracts: flatProductAddresses.flatMap(({ address: productAddr }) => 
      allAssetAddresses.map(assetAddr => ({
        address: assetAddr,
        abi: CONFIG.ERC20_ABI.abi as unknown as Abi,
        functionName: 'balanceOf',
        args: [productAddr],
      }))
    ),
  });

  // Get symbols for all assets
  const { data: allAssetSymbols } = useContractReads({
    contracts: allAssetAddresses.map(address => ({
      address,
      abi: CONFIG.ERC20_ABI.abi as unknown as Abi,
      functionName: 'symbol',
    })),
  });

  // Get item counts for each product
  const { data: itemCounts } = useContractReads({
    contracts: flatProductAddresses.map(({ address }) => ({
      address,
      abi: CONFIG.TRINITY_PRODUCT_ABI.abi as unknown as Abi,
      functionName: 'getItemCount',
    })),
  });

  // Get items for each product
  const { data: productItems } = useContractReads({
    contracts: flatProductAddresses.flatMap(({ address }, productIndex) => {
      const count = Number(itemCounts?.[productIndex]?.result || 0);
      return Array.from({ length: count }, (_, itemIndex) => ({
        address,
        abi: CONFIG.TRINITY_PRODUCT_ABI.abi as unknown as Abi,
        functionName: 'getItem',
        args: [itemIndex],
      }));
    }),
  });

  const processedData: EntityDetails[] = useMemo(() => 
    ((entityAddresses as `0x${string}`[]) || []).map((entityAddress, i) => {
      const entityName = entities?.[i]?.result as string;
      const entityType = entityTypes?.[i]?.result as string;
      const entityDescription = entityDescriptions?.[i]?.result as string;
      const entityOwner = entityOwners?.[i]?.result as `0x${string}`;
      const entityResellValue = entityResellValues?.[i]?.result as bigint || 0n;
      const entityProducts = productAddresses?.[i]?.result as `0x${string}`[];
      
      const products: ProductDetails[] = entityProducts?.map((productAddr) => {
        const productIndex = flatProductAddresses.findIndex(p => p.address === productAddr);
        const productName = productNames?.[productIndex]?.result as string;
        const productClassName = productClassNames?.[productIndex]?.result as string;
        const productDescription = productDescriptions?.[productIndex]?.result as string;
        const productCount = Number(productCounts?.[productIndex]?.result || 0);
        const productOwner = productOwners?.[productIndex]?.result as `0x${string}`;
        const productResellValue = productResellValues?.[productIndex]?.result as bigint || 0n;
        
        // Get items for this product
        const itemCount = Number(itemCounts?.[productIndex]?.result || 0);
        const items = Array.from({ length: itemCount }, (_, itemIndex) => {
          const itemData = productItems?.[productIndex * itemCount + itemIndex]?.result as [
            number,
            `0x${string}`,
            `0x${string}`,
            bigint,
            bigint,
            string,
            number,
            bigint
          ] | undefined;
          if (!itemData) return null;
          const [id, owner, asset, amount, createdAt, name, status, resellPrice] = itemData;
          console.log('Processing item data:', {
            id,
            owner,
            name,
            resellPrice: resellPrice.toString()
          });
          return {
            id,
            address: owner,
            name,
            description: '', // We'll need to get this from somewhere else
            owner,
            resellPrice
          };
        }).filter((item): item is NonNullable<typeof item> => item !== null) as {
          id: number;
          address: `0x${string}`;
          name: string;
          description: string;
          owner: `0x${string}`;
          resellPrice: bigint;
        }[];
        
        // Get supported assets for this product
        const supportedAssets = (productAssets?.[productIndex]?.result as `0x${string}`[]) || [];
        
        // Map assets to their balances and symbols
        const assets = supportedAssets.map((assetAddr, assetIndex) => {
          const balanceIndex = productIndex * allAssetAddresses.length + assetIndex;
          const symbolIndex = allAssetAddresses.indexOf(assetAddr);
          
          return {
            address: assetAddr,
            balance: (allAssetBalances?.[balanceIndex]?.result as bigint) || 0n,
            symbol: (allAssetSymbols?.[symbolIndex]?.result as string) || '',
          };
        });

        return {
          address: productAddr,
          name: productName || '',
          description: productDescription || '',
          className: productClassName || '',
          entityAddress,
          assets,
          productCount,
          owner: productOwner,
          resell_value: productResellValue,
          items
        };
      }) || [];

      return {
        address: entityAddress,
        name: entityName || '',
        type: entityType || '',
        description: entityDescription || '',
        products,
        owner: entityOwner,
        resell_value: entityResellValue,
      };
    }) || []
  , [
    entityAddresses, entities, entityTypes, entityDescriptions, 
    entityOwners, entityResellValues, productAddresses, 
    flatProductAddresses, productNames, productClassNames, 
    productDescriptions, productCounts, productOwners, 
    productResellValues, productAssets, allAssetAddresses, 
    allAssetBalances, allAssetSymbols, itemCounts, productItems
  ]);

  const isLoading = !entityAddresses || !entities || !entityTypes || !entityDescriptions || 
               !entityOwners || !entityResellValues || !productAddresses || 
               !productNames || !productClassNames || !productDescriptions || 
               !productCounts || !productOwners || !productResellValues || 
               !productAssets || !allAssetBalances || !allAssetSymbols ||
               !itemCounts || !productItems;

  return {
    entities: processedData,
    isLoading,
  };
} 